/**
 * Composer — the product centrepiece (WEB-2).
 *
 * Left: the raw material (notes, media, platforms, voice) and one CTA.
 * Right: a realistic preview per platform, inline editing with live counters
 * and validation, an AI toolbar, and per-platform schedule/publish controls.
 *
 * Routes: `/composer` (new) and `/composer/:postId` (edit). The dashboard and
 * calendar deep-link in with router state (`prefillTopic`, `prefillScheduledAt`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FlaskConical, Layers, Plug, Save, Sparkles, TriangleAlert } from 'lucide-react';
import type { GetPostResponse } from '../../shared/api';
import { PLATFORM_SPECS } from '../../shared/platforms';
import type {
  MediaAsset,
  PlatformId,
  PlatformPost,
  PostDetail,
  PublishAttempt,
} from '../../shared/types';
import { truncate } from '../../shared/utils';
import { PageHeader } from '../components/layout';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PlatformIcon,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  useToast,
} from '../components/ui';
import {
  BulkActionBar,
  MediaPickerDialog,
  PlatformEditor,
  PlatformPreviewSkeleton,
  SourcePane,
  addHashtags,
  hasBlockingIssue,
  issuesFor,
  platformOrder,
  useComposerDrafts,
} from '../components/composer';
import type { AiAction, BulkProgress, ComposerPrefill } from '../components/composer';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { formatWhen, postStatusLabel, POST_STATUS_TONE } from '../lib/format';
import { cn } from '../lib/utils';
import { useSessionStore } from '../stores/session';
import { useWorkspaceStore, useWorkspaceTimezone } from '../stores/workspace';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Turn a dashboard idea (title + angle + hook) into usable raw notes. */
function initialNotes(prefill: ComposerPrefill | null, topic: string | null): string {
  const lines: string[] = [];
  if (prefill?.prefillHook) lines.push(prefill.prefillHook);
  if (prefill?.prefillAngle) lines.push(prefill.prefillAngle);
  if (!lines.length && (prefill?.prefillTopic || topic)) {
    lines.push(prefill?.prefillTopic ?? topic ?? '');
  }
  return lines.join('\n\n');
}

/** Fallback copy when AI drafting is not configured for the deployment. */
function seedFromNotes(platform: PlatformId, notes: string): string {
  const spec = PLATFORM_SPECS[platform];
  const clean = notes.trim();
  if (spec.limits.unlimitedChars) return clean;
  return clean.length > spec.limits.maxChars ? truncate(clean, spec.limits.maxChars) : clean;
}

function isNotImplemented(error: unknown): boolean {
  return error instanceof ApiError && error.isNotImplemented;
}

function errorMessage(error: unknown): string | undefined {
  return error instanceof ApiError ? error.message : undefined;
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function ComposerPage() {
  const { postId } = useParams<{ postId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const timezone = useWorkspaceTimezone();
  const aiProvider = useSessionStore((state) => state.aiProvider);
  const simulatePublish = useSessionStore((state) => state.simulatePublish);

  const prefill = (location.state ?? null) as ComposerPrefill | null;
  const topicParam = searchParams.get('topic');
  const dateParam = searchParams.get('date');

  /* ---------------- source state ---------------- */

  const [title, setTitle] = useState(() => prefill?.prefillTopic ?? topicParam ?? '');
  const [rawContent, setRawContent] = useState(() => initialNotes(prefill, topicParam));
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [platforms, setPlatforms] = useState<PlatformId[]>(
    () => prefill?.prefillPlatforms?.filter((platform) => !PLATFORM_SPECS[platform]?.comingSoon) ?? [],
  );
  const [styleProfileId, setStyleProfileId] = useState('');
  const [instructions, setInstructions] = useState('');
  const [overwrite, setOverwrite] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removalCandidate, setRemovalCandidate] = useState<PlatformId | null>(null);
  const [aiBusy, setAiBusy] = useState<{ id: string; action: AiAction } | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);
  const [bulk, setBulk] = useState<BulkProgress>({
    running: false,
    done: 0,
    total: 0,
    current: null,
  });
  const [generatingImage, setGeneratingImage] = useState(false);

  const plannedFor = prefill?.prefillScheduledAt ?? dateParam ?? null;

  /* ---------------- queries ---------------- */

  const accountsQuery = useQuery({
    queryKey: qk.accounts(workspaceId),
    queryFn: () => api.accounts.list(),
    enabled: Boolean(workspaceId),
  });

  const styleQuery = useQuery({
    queryKey: qk.styleProfile(workspaceId),
    queryFn: () => api.style.profile(),
    enabled: Boolean(workspaceId),
    retry: false,
  });

  const postQuery = useQuery({
    queryKey: qk.post(workspaceId, postId ?? 'new'),
    queryFn: () => api.posts.get(postId as string),
    enabled: Boolean(workspaceId && postId),
  });

  const post = postQuery.data?.post ?? null;
  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);
  const platformStatuses = accountsQuery.data?.platforms ?? [];
  const styleProfiles = styleQuery.data?.profiles ?? [];

  const syncPost = useCallback(
    (next: PostDetail) => {
      queryClient.setQueryData<GetPostResponse>(qk.post(workspaceId, next.id), { post: next });
    },
    [queryClient, workspaceId],
  );

  const invalidateLists = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'posts'] });
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'notifications'] });
  }, [queryClient, workspaceId]);

  /* ---------------- draft buffer ---------------- */

  const drafts = useComposerDrafts({
    post,
    onSynced: syncPost,
    onError: (platform, error) => {
      toast({
        id: `save-${platform}`,
        title: `Could not save your ${PLATFORM_SPECS[platform].shortName} edit`,
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    },
  });

  /* ---------------- hydrate the source pane from the post ---------------- */

  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!post || hydratedRef.current === post.id) return;
    hydratedRef.current = post.id;
    setTitle(post.title ?? '');
    setRawContent(post.rawContent);
    setAssets(post.media);
    setPlatforms(post.platformPosts.map((variant) => variant.platform));
  }, [post]);

  const variants = useMemo(
    () =>
      [...(post?.platformPosts ?? [])].sort(
        (a, b) => platformOrder(a.platform) - platformOrder(b.platform),
      ),
    [post],
  );

  useEffect(() => {
    if (!variants.length) return;
    if (!variants.some((variant) => variant.platform === activeTab)) {
      setActiveTab(variants[0]?.platform ?? '');
    }
  }, [variants, activeTab]);

  /* ---------------- post autosave (title / notes / media) ---------------- */

  const savePost = useMutation({
    mutationFn: (input: { title: string; rawContent: string; mediaIds: string[] }) =>
      api.posts.update(post?.id ?? '', {
        title: input.title.trim() || null,
        rawContent: input.rawContent,
        mediaIds: input.mediaIds,
      }),
    onSuccess: (response) => syncPost(response.post),
    onError: (error) => {
      toast({
        id: 'save-post',
        title: 'Could not save your notes',
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    },
  });

  const sourceDirty = useMemo(() => {
    if (!post) return false;
    const sameMedia =
      post.media.length === assets.length &&
      post.media.every((asset, index) => asset.id === assets[index]?.id);
    return (
      (post.title ?? '') !== title.trim() || post.rawContent !== rawContent || !sameMedia
    );
  }, [post, title, rawContent, assets]);

  const generate = useMutation({
    mutationFn: async () => {
      await drafts.flush();
      const targets = platforms.filter((platform) => !PLATFORM_SPECS[platform].comingSoon);
      const mediaIds = assets.map((asset) => asset.id);

      let current: PostDetail;
      if (post) {
        const updated = await api.posts.update(post.id, {
          title: title.trim() || null,
          rawContent,
          mediaIds,
          platforms: targets,
        });
        current = updated.post;
      } else {
        const created = await api.posts.create({
          ...(title.trim() ? { title: title.trim() } : {}),
          rawContent,
          mediaIds,
          platforms: targets,
        });
        current = created.post;
      }

      try {
        const result = await api.posts.generate(current.id, {
          platforms: targets,
          ...(styleProfileId ? { styleProfileId } : {}),
          ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
          overwrite,
        });
        return { post: result.post, warnings: result.warnings, provider: result.provider, error: null };
      } catch (error) {
        return { post: current, warnings: [] as string[], provider: null, error };
      }
    },
    onSuccess: ({ post: next, warnings, provider, error }) => {
      syncPost(next);
      hydratedRef.current = next.id;
      setAssets(next.media);
      setPlatforms(next.platformPosts.map((variant) => variant.platform));
      invalidateLists();

      if (!postId) navigate(`/composer/${next.id}`, { replace: true, state: null });

      if (error) {
        if (isNotImplemented(error)) {
          for (const variant of next.platformPosts) {
            if (variant.content.trim()) continue;
            drafts.apply(
              variant.id,
              (current) => ({
                ...current,
                content: seedFromNotes(variant.platform, rawContent),
                meta: PLATFORM_SPECS[variant.platform].features.title
                  ? { ...current.meta, title: current.meta.title || title.trim() || 'Untitled' }
                  : current.meta,
              }),
              { immediate: true },
            );
          }
          toast({
            title: 'AI drafting is not configured',
            description:
              'No Gemini key or Vertex service account is set for this deployment, so your notes were dropped into each variant to edit by hand.',
            tone: 'warning',
            duration: 10_000,
          });
        } else {
          toast({
            title: 'Could not generate drafts',
            ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
            tone: 'error',
          });
        }
        return;
      }

      const first = next.platformPosts[0]?.platform;
      if (first) setActiveTab(first);

      toast({
        title: `Drafted ${next.platformPosts.length} variant${next.platformPosts.length === 1 ? '' : 's'}`,
        ...(provider === 'mock'
          ? { description: 'Demo AI — deterministic sample copy, no model was called.' }
          : {}),
        tone: 'success',
      });
      for (const warning of warnings.slice(0, 3)) {
        toast({ title: warning, tone: 'warning' });
      }
    },
    onError: (error) => {
      toast({
        title: 'Could not save this post',
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    },
  });

  /** "Save draft" before generating — keeps notes safe without spending AI. */
  const createDraft = useMutation({
    mutationFn: () =>
      api.posts.create({
        ...(title.trim() ? { title: title.trim() } : {}),
        rawContent,
        mediaIds: assets.map((asset) => asset.id),
        platforms: platforms.filter((platform) => !PLATFORM_SPECS[platform].comingSoon),
      }),
    onSuccess: (created) => {
      syncPost(created.post);
      hydratedRef.current = created.post.id;
      invalidateLists();
      navigate(`/composer/${created.post.id}`, { replace: true, state: null });
      toast({ title: 'Draft saved', tone: 'success', duration: 2500 });
    },
    onError: (error) => {
      toast({
        title: 'Could not save this draft',
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    },
  });

  const savePostMutate = savePost.mutate;
  const savePending = savePost.isPending;
  const generatePending = generate.isPending;

  useEffect(() => {
    if (!post || !sourceDirty || savePending || generatePending) return;
    const timer = window.setTimeout(() => {
      savePostMutate({ title, rawContent, mediaIds: assets.map((asset) => asset.id) });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [
    post,
    sourceDirty,
    title,
    rawContent,
    assets,
    savePostMutate,
    savePending,
    generatePending,
  ]);

  const sourceSaveState = savePost.isPending
    ? ('saving' as const)
    : sourceDirty
      ? ('dirty' as const)
      : savePost.isSuccess
        ? ('saved' as const)
        : ('clean' as const);

  /* ---------------- platform selection ---------------- */

  const applyPlatforms = useCallback(
    async (next: PlatformId[]) => {
      if (!post) return;
      try {
        const response = await api.posts.update(post.id, { platforms: next });
        syncPost(response.post);
        invalidateLists();
      } catch (error) {
        toast({
          title: 'Could not update the platforms',
          ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
          tone: 'error',
        });
      }
    },
    [post, syncPost, invalidateLists, toast],
  );

  const togglePlatform = (platform: PlatformId) => {
    const selected = platforms.includes(platform);
    const variant = post?.platformPosts.find((item) => item.platform === platform);

    if (selected && variant) {
      if (variant.status === 'published' || variant.status === 'publishing') {
        toast({
          title: `${PLATFORM_SPECS[platform].shortName} has already gone out`,
          description: 'Published variants stay on the post as a record.',
          tone: 'warning',
        });
        return;
      }
      if (variant.content.trim()) {
        setRemovalCandidate(platform);
        return;
      }
    }

    const next = selected
      ? platforms.filter((item) => item !== platform)
      : [...platforms, platform];
    setPlatforms(next);
    void applyPlatforms(next);
  };

  const confirmRemoval = async () => {
    if (!removalCandidate) return;
    const next = platforms.filter((item) => item !== removalCandidate);
    setPlatforms(next);
    setRemovalCandidate(null);
    await applyPlatforms(next);
  };

  /* ---------------- media ---------------- */

  const removeAsset = (assetId: string) =>
    setAssets((current) => current.filter((asset) => asset.id !== assetId));

  const moveAsset = (assetId: string, direction: -1 | 1) => {
    setAssets((current) => {
      const index = current.findIndex((asset) => asset.id === assetId);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  };

  const attachGeneratedImage = async (platform?: PlatformId, variantId?: string) => {
    setGeneratingImage(true);
    if (variantId) setAiBusy({ id: variantId, action: 'image' });
    try {
      const response = await api.ai.image({
        ...(post ? { postId: post.id } : {}),
        ...(platform ? { platform } : {}),
        ...(!post && rawContent.trim() ? { prompt: rawContent.trim() } : {}),
      });
      const asset = response.asset;
      setAssets((current) =>
        current.some((item) => item.id === asset.id) ? current : [...current, asset],
      );
      if (post) {
        const response2 = await api.posts.update(post.id, {
          mediaIds: [...assets.map((item) => item.id), asset.id],
        });
        syncPost(response2.post);
      }
      if (variantId) {
        drafts.apply(
          variantId,
          (current) => ({
            ...current,
            media: [...current.media, { assetId: asset.id }],
          }),
          { immediate: true },
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'media'] });
      toast({
        title: 'Image generated',
        description:
          response.provider === 'mock'
            ? 'Demo provider — a generated placeholder was stored in your library.'
            : 'Added to this post and your media library.',
        tone: 'success',
      });
    } catch (error) {
      toast({
        title: isNotImplemented(error) ? 'Image generation is not configured' : 'Could not generate an image',
        description: isNotImplemented(error)
          ? 'Add a Gemini API key or Vertex service account to enable it. Uploads still work.'
          : errorMessage(error),
        tone: isNotImplemented(error) ? 'warning' : 'error',
      });
    } finally {
      setGeneratingImage(false);
      setAiBusy(null);
    }
  };

  /* ---------------- per-variant actions ---------------- */

  const runAi = async (variant: PlatformPost, action: AiAction, instruction?: string) => {
    setAiBusy({ id: variant.id, action });
    try {
      if (action === 'improve') {
        const current = drafts.value(variant.id);
        const response = await api.ai.improve({
          content: current.content,
          instruction: instruction ?? 'Make this stronger.',
          platform: variant.platform,
        });
        drafts.apply(variant.id, (value) => ({ ...value, content: response.content }), {
          immediate: true,
        });
        toast({ title: 'Rewritten', tone: 'success', duration: 2500 });
      } else if (action === 'hashtags') {
        const current = drafts.value(variant.id);
        const response = await api.ai.hashtags({
          content: current.content,
          platform: variant.platform,
        });
        drafts.apply(variant.id, (value) => addHashtags(value, response.hashtags), {
          immediate: true,
        });
        toast({
          title: `Added ${response.hashtags.length} hashtag${response.hashtags.length === 1 ? '' : 's'}`,
          tone: 'success',
          duration: 2500,
        });
      } else if (action === 'regenerate') {
        const response = await api.platformPosts.regenerate(variant.id, {
          ...(instruction ? { instructions: instruction } : {}),
          ...(styleProfileId ? { styleProfileId } : {}),
        });
        drafts.reset(variant.id);
        if (post) {
          syncPost({
            ...post,
            platformPosts: post.platformPosts.map((item) =>
              item.id === variant.id ? response.platformPost : item,
            ),
          });
        }
        toast({
          title: `${PLATFORM_SPECS[variant.platform].shortName} redrafted`,
          ...(response.provider === 'mock' ? { description: 'Demo AI output.' } : {}),
          tone: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: isNotImplemented(error) ? 'AI is not configured' : 'That AI request failed',
        description: isNotImplemented(error)
          ? 'This deployment has no Gemini key or Vertex service account. Everything else in the composer still works.'
          : errorMessage(error),
        tone: isNotImplemented(error) ? 'warning' : 'error',
      });
    } finally {
      setAiBusy(null);
    }
  };

  const patchVariant = async (
    variant: PlatformPost,
    body: Parameters<typeof api.platformPosts.update>[1],
    successTitle?: string,
  ) => {
    setStatusBusyId(variant.id);
    try {
      const response = await api.platformPosts.update(variant.id, body);
      syncPost(response.post);
      invalidateLists();
      if (successTitle) toast({ title: successTitle, tone: 'success', duration: 3000 });
    } catch (error) {
      toast({
        title: 'That change did not stick',
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    } finally {
      setStatusBusyId(null);
    }
  };

  const toastAttempt = (attempt: PublishAttempt) => {
    const spec = PLATFORM_SPECS[attempt.platform];
    if (!attempt.ok) {
      toast({
        title: `${spec.shortName} did not publish`,
        ...(attempt.error ? { description: attempt.error } : {}),
        tone: 'error',
      });
      return;
    }
    const url = attempt.externalUrl;
    toast({
      title: `Published to ${spec.shortName}${attempt.simulated ? ' (simulated)' : ''}`,
      tone: 'success',
      ...(url
        ? { action: { label: 'View post', onClick: () => window.open(url, '_blank', 'noreferrer') } }
        : {}),
    });
  };

  const publishOne = async (variant: PlatformPost) => {
    setPublishingId(variant.id);
    try {
      await drafts.flush();
      const response = await api.platformPosts.publish(variant.id);
      syncPost(response.post);
      invalidateLists();
      toastAttempt(response.result);
    } catch (error) {
      toast({
        title: `Could not publish to ${PLATFORM_SPECS[variant.platform].shortName}`,
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    } finally {
      setPublishingId(null);
    }
  };

  /* ---------------- bulk actions ---------------- */

  /** Local attachments are the freshest view — autosave pushes them to the post. */
  const postAssets = assets;

  const blockedIds = variants
    .filter((variant) => hasBlockingIssue(issuesFor(variant, drafts.value(variant.id), postAssets)))
    .map((variant) => variant.id);

  const actionable = variants.filter(
    (variant) => variant.status !== 'published' && variant.status !== 'publishing',
  );

  const publishAll = async () => {
    await drafts.flush();
    const blocked = new Set(blockedIds);
    const targets = actionable.filter((variant) => !blocked.has(variant.id));
    if (!targets.length) {
      toast({ title: 'Nothing is ready to publish yet', tone: 'warning' });
      return;
    }

    setBulk({ running: true, done: 0, total: targets.length, current: targets[0]?.platform ?? null });
    let succeeded = 0;

    for (const [index, variant] of targets.entries()) {
      setBulk({ running: true, done: index, total: targets.length, current: variant.platform });
      try {
        const response = await api.platformPosts.publish(variant.id);
        syncPost(response.post);
        toastAttempt(response.result);
        if (response.result.ok) succeeded += 1;
      } catch (error) {
        toast({
          title: `${PLATFORM_SPECS[variant.platform].shortName} did not publish`,
          ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
          tone: 'error',
        });
      }
    }

    setBulk({ running: false, done: targets.length, total: targets.length, current: null });
    invalidateLists();
    toast({
      title:
        succeeded === targets.length
          ? `All ${targets.length} variant${targets.length === 1 ? '' : 's'} published`
          : `${succeeded} of ${targets.length} published`,
      tone: succeeded === targets.length ? 'success' : 'warning',
    });
  };

  const scheduleAll = useMutation({
    mutationFn: async ({ startIso, stagger }: { startIso: string; stagger: number }) => {
      if (!post) throw new ApiError(400, { error: 'bad_request', message: 'Save the post first.' });
      await drafts.flush();

      // A variant with a blocking issue will fail the moment the cron picks it
      // up — scheduling it just moves the error hours into the future, where
      // nobody is watching. Skip it here, the same way "Publish all" does.
      const blocked = new Set(blockedIds);
      const targets = actionable.filter((variant) => !blocked.has(variant.id));
      if (!targets.length) {
        throw new ApiError(400, {
          error: 'bad_request',
          message: 'Every variant still needs attention — fix the flagged issues first.',
        });
      }

      const start = Date.parse(startIso);
      const scheduleMap: Record<string, string> = {};
      targets.forEach((variant, index) => {
        scheduleMap[variant.id] = new Date(start + index * stagger * 60_000).toISOString();
      });
      return { response: await api.posts.schedule(post.id, { scheduleMap }), skipped: blocked.size };
    },
    onSuccess: ({ response, skipped }) => {
      syncPost(response.post);
      invalidateLists();
      toast({
        title: `Scheduled ${response.scheduled} variant${response.scheduled === 1 ? '' : 's'}`,
        description: skipped
          ? `Times shown in ${timezone}. ${skipped} variant${skipped === 1 ? '' : 's'} skipped — fix the flagged issues, then schedule ${skipped === 1 ? 'it' : 'them'}.`
          : `Times shown in ${timezone}.`,
        tone: skipped ? 'warning' : 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Could not schedule this post',
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    },
  });

  /* ---------------- simulated accounts (dev / unconfigured) ---------------- */

  const liveAccounts = accounts.filter((account) => !PLATFORM_SPECS[account.platform].comingSoon);
  const simulatable = platformStatuses.filter(
    (status) => status.canSimulate && !status.comingSoon && status.connectedCount === 0,
  );

  const createSimulated = useMutation({
    mutationFn: async () => {
      for (const status of simulatable) {
        await api.accounts.createSimulated({ platform: status.platform });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.accounts(workspaceId) });
      toast({
        title: 'Simulated accounts ready',
        description: 'Publishing runs end to end without touching a live network.',
        tone: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Could not create simulated accounts',
        ...(errorMessage(error) ? { description: errorMessage(error) } : {}),
        tone: 'error',
      });
    },
  });

  /* ---------------- render ---------------- */

  const accountForVariant = (variant: PlatformPost) =>
    accounts.find((account) => account.id === variant.socialAccountId) ??
    accounts.find((account) => account.platform === variant.platform) ??
    null;

  const loadingPost = Boolean(postId) && postQuery.isPending;
  const postMissing = Boolean(postId) && postQuery.isError;

  const headerTitle = loadingPost
    ? 'Loading post…'
    : post?.title?.trim() || (postId ? 'Untitled post' : 'New post');

  return (
    <>
      <PageHeader
        title={headerTitle}
        description="Drop in notes, a link or a topic. Pick your platforms. Get a tailored draft for each one."
        {...(postId ? { backTo: '/posts', backLabel: 'All posts' } : {})}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {post && (
              <Badge tone={POST_STATUS_TONE[post.status]} dot>
                {postStatusLabel(post.status)}
              </Badge>
            )}
            {simulatePublish && (
              <Tooltip content="SIMULATE_PUBLISH is on — nothing reaches a live network.">
                <Badge tone="warning">
                  <FlaskConical className="size-3" aria-hidden />
                  Simulation mode
                </Badge>
              </Tooltip>
            )}
            {aiProvider === 'mock' && (
              <Tooltip content="No Gemini credentials configured — AI output is deterministic demo copy.">
                <Badge tone="neutral">
                  <Sparkles className="size-3" aria-hidden />
                  Demo AI
                </Badge>
              </Tooltip>
            )}
            {!post && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Save className="size-3.5" />}
                disabled={!rawContent.trim() || generate.isPending}
                loading={createDraft.isPending}
                onClick={() => createDraft.mutate()}
              >
                Save draft
              </Button>
            )}
          </div>
        }
      />

      {postMissing ? (
        <EmptyState
          icon={<TriangleAlert className="size-5" />}
          title="That post is no longer here"
          description="It may have been deleted from another tab or by a teammate."
          action={
            <Button leftIcon={<ArrowLeft className="size-4" />} onClick={() => navigate('/composer')}>
              Start a new post
            </Button>
          }
        />
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-12">
          {/* ---------------- left: source ---------------- */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="lg:sticky lg:top-20 lg:max-h-[calc(100dvh-7rem)] lg:overflow-y-auto lg:pr-1">
              {loadingPost ? (
                <div className="pap-card flex items-center justify-center py-24">
                  <Spinner label="Loading this post" />
                </div>
              ) : (
                <SourcePane
                  title={title}
                  onTitleChange={setTitle}
                  rawContent={rawContent}
                  onRawContentChange={setRawContent}
                  assets={assets}
                  onAddMedia={() => setPickerOpen(true)}
                  onRemoveMedia={removeAsset}
                  onMoveMedia={moveAsset}
                  onGenerateImage={() => void attachGeneratedImage()}
                  generatingImage={generatingImage}
                  accounts={accounts}
                  platformStatuses={platformStatuses}
                  selectedPlatforms={platforms}
                  onTogglePlatform={togglePlatform}
                  connectionNotice={
                    accountsQuery.isSuccess && liveAccounts.length === 0 ? (
                      <Alert
                        tone="warning"
                        title="No accounts connected yet"
                        action={
                          simulatable.length > 0 ? (
                            <Button
                              size="xs"
                              variant="secondary"
                              loading={createSimulated.isPending}
                              onClick={() => createSimulated.mutate()}
                            >
                              Simulate {simulatable.length}
                            </Button>
                          ) : (
                            <Link
                              to="/connections"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"
                            >
                              <Plug className="size-3.5" aria-hidden />
                              Connect
                            </Link>
                          )
                        }
                      >
                        {simulatable.length > 0
                          ? 'Connect a real account, or spin up simulated ones to explore the whole flow without any API keys.'
                          : 'Connect at least one platform before drafting.'}
                      </Alert>
                    ) : null
                  }
                  styleProfiles={styleProfiles}
                  styleProfileId={styleProfileId}
                  onStyleProfileChange={setStyleProfileId}
                  instructions={instructions}
                  onInstructionsChange={setInstructions}
                  overwrite={overwrite}
                  onOverwriteChange={setOverwrite}
                  hasExistingDrafts={variants.some((variant) => variant.content.trim().length > 0)}
                  onGenerate={() => generate.mutate()}
                  generating={generate.isPending}
                  saveState={sourceSaveState}
                  aiProvider={aiProvider}
                />
              )}
            </div>
          </div>

          {/* ---------------- right: previews ---------------- */}
          <div className="space-y-4 lg:col-span-7 xl:col-span-8">
            {plannedFor && !loadingPost && !variants.some((variant) => variant.scheduledAt) && (
              <Alert tone="info" title={`Planned for ${formatWhen(plannedFor, timezone)}`}>
                You came from the calendar — use “Schedule all” once the drafts look right.
              </Alert>
            )}

            {loadingPost ? (
              <div className="space-y-4">
                {(['x', 'instagram'] as PlatformId[]).map((platform) => (
                  <PlatformPreviewSkeleton key={platform} platform={platform} />
                ))}
              </div>
            ) : generate.isPending ? (
              <div className="space-y-4">
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Sparkles className="size-4 animate-pulse text-accent" aria-hidden />
                  Writing {platforms.length} draft{platforms.length === 1 ? '' : 's'} in your voice…
                </p>
                {(platforms.length ? platforms : (['x'] as PlatformId[])).map((platform) => (
                  <PlatformPreviewSkeleton key={platform} platform={platform} />
                ))}
              </div>
            ) : variants.length === 0 ? (
              <EmptyState
                className="min-h-[24rem]"
                icon={<Layers className="size-5" />}
                title="Your per-platform drafts land here"
                description="Paste your notes, choose the platforms you publish to, and generate. Each one gets its own copy, hashtags and preview — edit any of them inline before you schedule."
                secondaryAction={
                  <Link
                    to="/style"
                    className="text-sm text-accent underline-offset-4 hover:underline"
                  >
                    Teach it your writing voice
                  </Link>
                }
              />
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="sticky top-14 z-10 -mx-1 bg-bg/85 px-1 backdrop-blur">
                  {variants.map((variant) => {
                    const blocked = blockedIds.includes(variant.id);
                    return (
                      <TabsTrigger
                        key={variant.id}
                        value={variant.platform}
                        badge={
                          <span
                            className={cn(
                              'size-1.5 rounded-full',
                              variant.status === 'published'
                                ? 'bg-success'
                                : variant.status === 'failed'
                                  ? 'bg-danger'
                                  : blocked
                                    ? 'bg-warning'
                                    : variant.status === 'scheduled'
                                      ? 'bg-info'
                                      : 'bg-border-strong',
                            )}
                            aria-hidden
                          />
                        }
                      >
                        <PlatformIcon platform={variant.platform} size="xs" variant="soft" />
                        {PLATFORM_SPECS[variant.platform].shortName}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {variants.map((variant) => (
                  <TabsContent key={variant.id} value={variant.platform}>
                    <PlatformEditor
                      variant={variant}
                      value={drafts.value(variant.id)}
                      saveState={drafts.state(variant.id)}
                      account={accountForVariant(variant)}
                      accounts={accounts}
                      postAssets={postAssets}
                      timezone={timezone}
                      busyAi={aiBusy?.id === variant.id ? aiBusy.action : null}
                      publishing={publishingId === variant.id}
                      statusBusy={statusBusyId === variant.id}
                      onChange={(next, options) => drafts.apply(variant.id, next, options)}
                      onImprove={(instruction) => void runAi(variant, 'improve', instruction)}
                      onRegenerate={(steer) => void runAi(variant, 'regenerate', steer)}
                      onHashtags={() => void runAi(variant, 'hashtags')}
                      onGenerateImage={() => void attachGeneratedImage(variant.platform, variant.id)}
                      onApproveChange={(approved) =>
                        void patchVariant(
                          variant,
                          { status: approved ? 'approved' : 'draft' },
                          approved ? `${PLATFORM_SPECS[variant.platform].shortName} approved` : undefined,
                        )
                      }
                      onSchedule={(isoValue) =>
                        void patchVariant(
                          variant,
                          { scheduledAt: isoValue },
                          isoValue
                            ? `${PLATFORM_SPECS[variant.platform].shortName} scheduled for ${formatWhen(isoValue, timezone)}`
                            : undefined,
                        )
                      }
                      onPublishNow={() => void publishOne(variant)}
                      onSelectAccount={(accountId) =>
                        void patchVariant(variant, { socialAccountId: accountId })
                      }
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}

            {variants.length > 0 && actionable.length > 0 && !generate.isPending && (
              <BulkActionBar
                variants={actionable}
                timezone={timezone}
                progress={bulk}
                scheduling={scheduleAll.isPending}
                blockedIds={blockedIds}
                defaultStartIso={plannedFor}
                onPublishAll={() => void publishAll()}
                onScheduleAll={(startIso, stagger) => scheduleAll.mutate({ startIso, stagger })}
              />
            )}
          </div>
        </div>
      )}

      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={assets}
        onConfirm={setAssets}
      />

      <ConfirmDialog
        open={Boolean(removalCandidate)}
        onOpenChange={(open) => !open && setRemovalCandidate(null)}
        title={
          removalCandidate
            ? `Remove the ${PLATFORM_SPECS[removalCandidate].shortName} variant?`
            : 'Remove this variant?'
        }
        description="Its draft copy, hashtags and schedule will be deleted. The other platforms are untouched."
        confirmLabel="Remove variant"
        destructive
        onConfirm={confirmRemoval}
      />
    </>
  );
}

export default ComposerPage;
